import { useRef, useState } from "react";
import { importCsv } from "../api/client";
import { showApiError } from "@/lib/error-toast";

interface CsvImportDialogProps {
  tableId: string;
  onClose: () => void;
  onImported: () => void;
}

type Mode = "file" | "paste";

export function CsvImportDialog({ tableId, onClose, onImported }: CsvImportDialogProps) {
  const [mode, setMode] = useState<Mode>("file");
  const [file, setFile] = useState<File | null>(null);
  const [pasteText, setPasteText] = useState("");
  const [firstRowIsHeader, setFirstRowIsHeader] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ imported: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const canSubmit =
    !isSubmitting &&
    ((mode === "file" && file !== null) ||
      (mode === "paste" && pasteText.trim().length > 0));

  async function handleImport() {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setResult(null);
    try {
      const res =
        mode === "file"
          ? await importCsv(tableId, { file: file! }, { firstRowIsHeader })
          : await importCsv(tableId, { text: pasteText }, { firstRowIsHeader });
      setResult({ imported: res.imported });
      onImported();
    } catch (err) {
      showApiError(err, { fallback: "CSV import failed." });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="lt-dialog-backdrop" onClick={onClose}>
      <div className="lt-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="lt-dialog-title">Import CSV</h2>

        <div
          className="lt-dialog-field"
          role="tablist"
          aria-label="CSV input mode"
          style={{ display: "flex", gap: 8 }}
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === "file"}
            className={`rb-btn ${mode === "file" ? "rb-btn-save" : "rb-btn-cancel"}`}
            onClick={() => setMode("file")}
          >
            Upload file
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "paste"}
            className={`rb-btn ${mode === "paste" ? "rb-btn-save" : "rb-btn-cancel"}`}
            onClick={() => setMode("paste")}
          >
            Paste CSV
          </button>
        </div>

        {mode === "file" ? (
          <div className="lt-dialog-field">
            <label className="lt-dialog-label" htmlFor="csv-file-input">
              CSV File
            </label>
            <input
              id="csv-file-input"
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
        ) : (
          <div className="lt-dialog-field">
            <label className="lt-dialog-label" htmlFor="csv-paste-input">
              CSV text
            </label>
            <textarea
              id="csv-paste-input"
              className="lt-dialog-input"
              rows={8}
              spellCheck={false}
              placeholder={"code,name\nA,Alpha\nB,Beta"}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              style={{ fontFamily: "ui-monospace, monospace", width: "100%" }}
            />
          </div>
        )}

        <div className="lt-dialog-field lt-dialog-checkbox-row">
          <input
            id="first-row-header"
            type="checkbox"
            checked={firstRowIsHeader}
            onChange={(e) => setFirstRowIsHeader(e.target.checked)}
          />
          <label className="lt-dialog-label" htmlFor="first-row-header">
            First row is header
          </label>
        </div>

        {result && (
          <div className="lt-import-success">
            Imported {result.imported} row{result.imported === 1 ? "" : "s"} successfully.
          </div>
        )}

        <div className="lt-dialog-footer">
          <button
            type="button"
            className="rb-btn rb-btn-cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rb-btn rb-btn-save"
            disabled={!canSubmit}
            onClick={() => void handleImport()}
          >
            {isSubmitting ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}
